
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CaseStudy } from '../types';
import SmartImage from './SmartImage';
import PrivacyPolicy from './PrivacyPolicy';
import PrivacyPolicy3DPrintForge from './PrivacyPolicy3DPrintForge';
import PrivacyPolicyVamosly from './PrivacyPolicyVamosly';
import AccountDeletion from './AccountDeletion';
import AccountDeletionRideRoster from './AccountDeletionRideRoster';
import AccountDeletionVamosly from './AccountDeletionVamosly';
import TermsAndConditions from './TermsAndConditions';
import TermsAndConditions3DPrintForge from './TermsAndConditions3DPrintForge';
import TermsAndConditionsVamosly from './TermsAndConditionsVamosly';

interface ProjectDetailOverlayProps {
  study: CaseStudy | null;
  onClose: () => void;
}

const ProjectDetailOverlay: React.FC<ProjectDetailOverlayProps> = ({ study, onClose }) => {
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showAccountDeletion, setShowAccountDeletion] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  if (!study) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl animate-in fade-in duration-500 overflow-y-auto">
      <button 
        onClick={onClose}
        className="fixed top-8 right-8 z-[210] size-14 bg-white text-black flex items-center justify-center hover:bg-primary hover:text-white transition-all group"
      >
        <span className="material-symbols-outlined transform group-hover:rotate-90 transition-transform">close</span>
      </button>

      <div className="max-w-[1440px] mx-auto px-6 lg:px-20 py-32">
        <div className="grid lg:grid-cols-2 gap-20 items-start mb-32">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="text-primary font-black uppercase tracking-[0.5em] text-sm block">
                {study.comingSoon ? 'Future Project' : 'Featured Project'}
              </span>
              {study.comingSoon && (
                <span className="bg-primary text-white text-[10px] font-black uppercase tracking-widest px-3 py-1">In Development</span>
              )}
            </div>
            <h2 className="text-7xl lg:text-9xl font-black uppercase tracking-tighter leading-none mb-12 italic">
              {study.title}
            </h2>
            <div className="flex gap-4 mb-12">
              <span className="px-4 py-2 border border-white/20 text-xs font-bold uppercase tracking-widest">{study.category}</span>
              <span className="px-4 py-2 border border-white/20 text-xs font-bold uppercase tracking-widest">{study.platform || 'Mobile App'}</span>
            </div>
            <p className="text-xl lg:text-3xl text-white/60 font-light leading-relaxed">
              {study.description}
            </p>
          </div>
          <div className="aspect-[4/5] bg-zinc-900 border border-white/10 overflow-hidden relative">
             <SmartImage src={study.imageUrl} alt={study.title} className="w-full h-full object-cover grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all duration-700" />
             {study.comingSoon && (
               <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                 <p className="text-4xl font-black uppercase tracking-[0.2em] italic text-primary select-none">Coming 2026</p>
               </div>
             )}
          </div>
        </div>

        {!study.comingSoon && study.screenshots && study.screenshots.length > 0 && (
          <div>
            <h3 className="text-2xl font-black uppercase tracking-widest mb-12 flex items-center gap-4">
              <span className="w-12 h-px bg-primary"></span>
              Interface Gallery
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {study.screenshots.map((src, i) => (
                <div key={i} className="bg-zinc-900 border border-white/5 aspect-[9/16] overflow-hidden group">
                  <img 
                    src={src} 
                    alt={`${study.title} screenshot ${i + 1}`} 
                    className="w-full h-full object-cover scale-100 group-hover:scale-110 transition-transform duration-700" 
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-40 pt-20 border-t border-white/10 text-center flex flex-col md:flex-row items-center justify-center flex-wrap gap-6">
           <button 
             disabled={study.comingSoon}
             className={`${study.comingSoon ? 'bg-white/10 text-white/20 cursor-not-allowed' : 'bg-primary text-white hover:bg-white hover:text-black'} px-16 py-8 font-black text-xl tracking-[0.2em] uppercase transition-all`}
           >
              {study.comingSoon ? 'Project Locked' : 'Launch App Store'}
           </button>
           
           {study.hasPrivacyPolicy && (
             <button 
               onClick={(e) => {
                 e.preventDefault();
                 e.stopPropagation();
                 setShowPrivacyPolicy(true);
               }}
               className="px-16 py-8 border border-white/20 text-white hover:bg-white hover:text-black font-black text-xl tracking-[0.2em] uppercase transition-all"
             >
               Privacy Policy
             </button>
           )}

           {study.hasAccountDeletion && (
             <button 
               onClick={(e) => {
                 e.preventDefault();
                 e.stopPropagation();
                 setShowAccountDeletion(true);
               }}
               className="px-16 py-8 border border-white/20 text-white hover:bg-white hover:text-black font-black text-xl tracking-[0.2em] uppercase transition-all"
             >
               Delete Account
             </button>
           )}

           {study.hasTermsAndConditions && (
             <button 
               onClick={(e) => {
                 e.preventDefault();
                 e.stopPropagation();
                 setShowTerms(true);
               }}
               className="px-16 py-8 border border-white/20 text-white hover:bg-white hover:text-black font-black text-xl tracking-[0.2em] uppercase transition-all"
             >
               Terms & Conditions
             </button>
           )}
        </div>
      </div>

      {showPrivacyPolicy && document.body && createPortal(
        study.id === '6' ? (
          <PrivacyPolicy3DPrintForge 
            appName={study.title} 
            onClose={() => setShowPrivacyPolicy(false)} 
          />
        ) : study.id === '14' ? (
          <PrivacyPolicyVamosly
            appName={study.title}
            onClose={() => setShowPrivacyPolicy(false)}
          />
        ) : (
          <PrivacyPolicy 
            appName={study.title} 
            onClose={() => setShowPrivacyPolicy(false)} 
          />
        ),
        document.body
      )}

      {showAccountDeletion && document.body && createPortal(
        study.id === '5' ? (
          <AccountDeletionRideRoster 
            appName={study.title} 
            onClose={() => setShowAccountDeletion(false)} 
          />
        ) : study.id === '14' ? (
          <AccountDeletionVamosly 
            appName={study.title} 
            onClose={() => setShowAccountDeletion(false)} 
          />
        ) : (
          <AccountDeletion 
            appName={study.title} 
            onClose={() => setShowAccountDeletion(false)} 
          />
        ),
        document.body
      )}

      {showTerms && document.body && createPortal(
        study.id === '6' ? (
          <TermsAndConditions3DPrintForge 
            appName={study.title} 
            onClose={() => setShowTerms(false)} 
          />
        ) : study.id === '14' ? (
          <TermsAndConditionsVamosly
            appName={study.title}
            onClose={() => setShowTerms(false)}
          />
        ) : (
          <TermsAndConditions 
            appName={study.title} 
            onClose={() => setShowTerms(false)} 
          />
        ),
        document.body
      )}
    </div>
  );
};

export default ProjectDetailOverlay;